import type {
  ApplyResetPasswordRequest,
  ChangePasswordPayload,
  ConnectRequest,
  ConnectResponse,
  LoginRequest,
  RegisterRequest,
  ResetPasswordPayload,
  TokenResponse,
} from "./model"
import { updateTokens } from "./slice"
import { withRefreshLock } from "./refreshLock"
import baseApi from "@/base/baseApi"

/**
 * Stores the tokens a response carries under the refresh lock, so the write can't land in the
 * middle of another tab's refresh. Only the write: an expired JWT sends the request itself through
 * the refresh, which takes the same lock, so holding it across `queryFulfilled` would deadlock.
 */
const storeTokens = async (
  dispatch: (action: ReturnType<typeof updateTokens>) => unknown,
  queryFulfilled: PromiseLike<{ data: TokenResponse }>,
) => {
  let response

  try {
    response = await queryFulfilled
  } catch {
    return
  }

  await withRefreshLock(() => {
    dispatch(updateTokens(response.data))
  })
}

const api = baseApi.injectEndpoints({
  endpoints: (build) => ({
    login: build.mutation<TokenResponse, LoginRequest>({
      query: (body) => ({
        url: "security/login",
        method: "POST",
        body,
      }),
      extraOptions: { withoutAuth: true },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await storeTokens(dispatch, queryFulfilled)
      },
    }),
    connectOauth: build.mutation<ConnectResponse, ConnectRequest>({
      query: ({ provider, ...body }) => ({
        url: `oauth/connect/${provider}`,
        method: "POST",
        body,
      }),
      extraOptions: { withoutAuth: true },
    }),
    register: build.mutation<TokenResponse, RegisterRequest>({
      query: (body) => ({
        url: "register",
        method: "POST",
        body,
      }),
      extraOptions: { withoutAuth: true },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await storeTokens(dispatch, queryFulfilled)
      },
    }),
    requestResetPassword: build.mutation<void, ResetPasswordPayload>({
      query: (body) => ({
        url: "request-reset-password",
        method: "POST",
        body,
      }),
      extraOptions: { withoutAuth: true },
    }),
    applyResetPassword: build.mutation<
      TokenResponse,
      ApplyResetPasswordRequest
    >({
      query: (body) => ({
        url: "apply-reset-password",
        method: "POST",
        body,
      }),
      extraOptions: { withoutAuth: true },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await storeTokens(dispatch, queryFulfilled)
      },
    }),
    applyVerifyEmail: build.mutation<void, Record<string, string>>({
      query: (params) => ({
        url: "verify-email",
        method: "POST",
        params,
      }),
      extraOptions: { withoutAuth: true },
    }),
    changePassword: build.mutation<TokenResponse, ChangePasswordPayload>({
      query: (body) => ({
        url: "change-password",
        method: "POST",
        body,
      }),
      // Changing the password revokes every session, this one included; the response carries
      // its replacement.
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await storeTokens(dispatch, queryFulfilled)
      },
    }),
    logout: build.mutation<void, string>({
      query: (payload) => ({
        url: "security/invalidate",
        method: "POST",
        body: { refreshToken: payload },
      }),
    }),
  }),
})

export const {
  useLoginMutation,
  useConnectOauthMutation,
  useRegisterMutation,
  useRequestResetPasswordMutation,
  useApplyResetPasswordMutation,
  useApplyVerifyEmailMutation,
  useChangePasswordMutation,
  useLogoutMutation,
} = api
